import type { Response } from './types';

let CSRF_TOKEN = '';

export const HOST = import.meta.env.TEST_HOST ?? null;

const headers = {
  'Accept': 'application/json',
  'Content-Type': 'application/json',
  'X-CSRF-TOKEN': '',
};

const setCSRF = (token: string) => {
  CSRF_TOKEN = token;
};

const unsetCSRF = () => {
  CSRF_TOKEN = '';
};

/**
 * Returns the current CSRF token held in module state.
 *
 * Exposed so the configured `ky` instance in `lib/request/api.ts` can share the
 * same token store as this request helper instead of maintaining a parallel one.
 *
 * @return {string} The current CSRF token (empty string before `setCSRF` runs).
 */
export const getCSRF = (): string => CSRF_TOKEN;

const getHeaders = (): Record<keyof typeof headers, string> => ({
  ...headers,
  'X-CSRF-TOKEN': CSRF_TOKEN,
});

const runFetch = async <OkApiResponse, FailApiResponse>(
  url: string,
  payload: unknown,
  method: 'POST' | 'PATCH' | 'DELETE' | 'PUT',
  abortController: AbortController,
  onNoAuth: () => void
): Promise<Response<OkApiResponse, FailApiResponse>> => {
  try {
    const reqUrl = HOST ? `${HOST}${url}` : url;
    const response = await fetch(reqUrl, {
      method,
      headers: getHeaders(),
      body: JSON.stringify(payload), // body data type must match "Content-Type" header
      signal: abortController.signal,
    });

    if (response.ok) {
      // Handle 204 No Content responses (empty body)
      if (
        (method === 'PATCH' || method === 'DELETE') &&
        response.status === 204
      ) {
        return {
          type: 'success',
          data: null as OkApiResponse,
        };
      }

      return {
        type: 'success',
        data:
          method === 'POST' ||
          method === 'DELETE' ||
          method === 'PATCH' ||
          method === 'PUT'
            ? await response.json()
            : null,
      };
    }

    if (response.status === 401) {
      onNoAuth();
    }

    if (response.status >= 500) {
      return { type: 'fatal', message: 'Internal Server Error' };
    }

    try {
      return {
        type: 'error',
        data: await response.json(),
      };
    } catch (_e) {
      return { type: 'fatal', message: response.statusText };
    }
  } catch (e) {
    if (e instanceof Error) {
      if (e.name === 'AbortError') {
        return { type: 'abort', message: 'Request aborted' };
      }
    }
    return { type: 'fatal', message: 'Failed to fetch resource' };
  }
};

const post = async <OkApiResponse, FailApiResponse>(
  url: string,
  payload: unknown,
  abortController: AbortController,
  onNoAuth?: () => void
): Promise<Response<OkApiResponse, FailApiResponse>> => {
  return await runFetch(
    url,
    payload,
    'POST',
    abortController,
    onNoAuth ? onNoAuth : () => location.reload()
  );
};

const patch = async <OkApiResponse, FailApiResponse>(
  url: string,
  payload: unknown,
  abortController: AbortController,
  onNoAuth?: () => void
): Promise<Response<OkApiResponse, FailApiResponse>> => {
  return await runFetch(
    url,
    payload,
    'PATCH',
    abortController,
    onNoAuth ? onNoAuth : () => location.reload()
  );
};

const put = async <OkApiResponse, FailApiResponse>(
  url: string,
  payload: unknown,
  abortController: AbortController,
  onNoAuth?: () => void
): Promise<Response<OkApiResponse, FailApiResponse>> => {
  return await runFetch(
    url,
    payload,
    'PUT',
    abortController,
    onNoAuth ? onNoAuth : () => location.reload()
  );
};

const deleteReq = async <OkApiResponse, FailApiResponse>(
  url: string,
  payload: unknown,
  abortController: AbortController,
  onNoAuth?: () => void
): Promise<Response<OkApiResponse, FailApiResponse>> => {
  return await runFetch(
    url,
    payload,
    'DELETE',
    abortController,
    onNoAuth ? onNoAuth : () => location.reload()
  );
};

const get = async <ApiResponse = unknown>(
  url: string,
  abortController?: AbortController
): Promise<ApiResponse | { error: string }> => {
  try {
    const reqUrl = HOST ? `${HOST}${url}` : url;
    const response = await fetch(reqUrl, {
      method: 'GET',
      headers: getHeaders(),
      signal: abortController?.signal,
    });

    if (response.ok) {
      return await response.json();
    }

    if (response.status >= 500) {
      return { error: 'Internal Server Error' };
    }

    // A 4xx body carries the API's own sentence, and callers have to be able to read it: the
    // vesting candidates panel turns "…an as_of date is required" into a date prompt for the
    // user, which it cannot do from a generic string. Discarding the body here made that refusal
    // arrive as "Unknown", so the prompt never appeared and the tract could not be linked at all.
    //
    // The mutating verbs above already return the parsed 4xx body; this brings GET into line
    // rather than keeping two conventions in one file.
    //
    // Always narrowed to { error: string } rather than returned raw — an error body shaped
    // unlike the API's would otherwise reach a caller that is typed for a success.
    try {
      const body = (await response.json()) as {
        error?: unknown;
        errors?: { message?: unknown }[];
      } | null;
      const message = [body?.error, body?.errors?.[0]?.message].find(
        (value): value is string => typeof value === 'string'
      );

      return { error: message || response.statusText || 'Unknown' };
    } catch (_e) {
      return { error: response.statusText || 'Unknown' };
    }
  } catch (e) {
    if (e instanceof Error && e.name === 'AbortError') {
      return { error: e.name };
    }
    return { error: 'Failed to fetch resource' };
  }
};

export { deleteReq, get, patch, put, post, setCSRF, unsetCSRF };
